import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../API/customAxiosBaseQuery';
import {myAccountsAPI} from '../API/api';
import {BaseResponseType} from '../types/TypesStore';
import {
	Account,
	ConversionCreateType,
	ConversionDataType,
	CreateDataTransfer,
	CreateDataWithdrawal,
	RequestBillingTransactions,
	RequestPremiaType,
	ResponsePointSmpType,
	ResponsePremiaType,
	Transaction,
	Transfer,
	Withdrawal,
} from '../types/TypesUserAccounts';
import {
	createQueryEndpoint,
	createMutationEndpoint,
	createSimpleQueryEndpoint,
} from './utils/apiFactory';

export const userBillingApi = createApi({
	reducerPath: 'userBillingApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'billing-list',
		'sum-bonus',
		'premia',
		'pointSmp',
		'optima',
		'cashback',
		'conversion',
		'billing-transfer',
		'billing-withdrawal',
		'billing-transactions-list',
	],
	endpoints: (builder) => ({
		billingList: builder.query<
			BaseResponseType & {currencies: Account[]},
			string
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingList, ['billing-list']),
		}),
		getSumBonus: builder.query<
			BaseResponseType & {sum_bonus_money: string},
			string
		>({
			...createQueryEndpoint(myAccountsAPI.getSumBonus, ['sum-bonus']),
		}),
		getPremia: builder.query<
			BaseResponseType & ResponsePremiaType,
			RequestPremiaType
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingPremia, ['premia']),
		}),
		getBillingOptima: builder.query<
			BaseResponseType & ResponsePremiaType,
			RequestPremiaType
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingOptima, ['optima']),
		}),
		getBillingCashback: builder.query<
			BaseResponseType & ResponsePremiaType,
			RequestPremiaType
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingCashback, ['cashback']),
		}),
		getBillingPointSmp: builder.query<
			BaseResponseType & ResponsePointSmpType,
			{month: string}
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingPointSmp, ['pointSmp']),
		}),
		getConversion: builder.query<
			BaseResponseType & {accounts: ConversionDataType[]},
			void
		>({
			...createSimpleQueryEndpoint(myAccountsAPI.getConversion, ['conversion']),
		}),
		getConversionCreate: builder.mutation<
			BaseResponseType & ResponsePointSmpType,
			ConversionCreateType
		>({
			...createMutationEndpoint(myAccountsAPI.getConversionCreate, [
				'sum-bonus',
				'billing-list',
				'conversion',
			]),
		}),
		transferCreate: builder.mutation<BaseResponseType, CreateDataTransfer>({
			...createMutationEndpoint(myAccountsAPI.transferCreate, [
				'sum-bonus',
				'billing-list',
			]),
		}),
		withdrawalCreate: builder.mutation<BaseResponseType, CreateDataWithdrawal>({
			...createMutationEndpoint(myAccountsAPI.withdrawalCreate, [
				'sum-bonus',
				'billing-list',
			]),
		}),
		getBillingTransfer: builder.query<BaseResponseType & Transfer, void>({
			...createSimpleQueryEndpoint(myAccountsAPI.getBillingTransfer, [
				'billing-transfer',
			]),
		}),
		withdrawal: builder.query<BaseResponseType & Withdrawal, void>({
			...createSimpleQueryEndpoint(myAccountsAPI.withdrawal, [
				'billing-withdrawal',
			]),
		}),
		billingTransactionsList: builder.query<
			BaseResponseType & {transactions: Transaction[]},
			RequestBillingTransactions
		>({
			...createQueryEndpoint(myAccountsAPI.getBillingTransactions, [
				'billing-transactions-list',
			]),
		}),
		billingApprove: builder.mutation<BaseResponseType, number>({
			...createMutationEndpoint(myAccountsAPI.billingApprove, [
				'billing-transactions-list',
			]),
		}),
		billingReject: builder.mutation<BaseResponseType, number>({
			...createMutationEndpoint(myAccountsAPI.billingReject, [
				'billing-transactions-list',
			]),
		}),
		billingConfirm: builder.mutation<BaseResponseType, number>({
			...createMutationEndpoint(myAccountsAPI.billingConfirm, [
				'billing-transactions-list',
			]),
		}),
		billingReceive: builder.mutation<BaseResponseType, number>({
			...createMutationEndpoint(myAccountsAPI.billingReceive, [
				'billing-transactions-list',
			]),
		}),
	}),
});

export const {
	useBillingListQuery,
	useGetSumBonusQuery,
	useGetPremiaQuery,
	useGetBillingOptimaQuery,
	useGetBillingCashbackQuery,
	useGetBillingPointSmpQuery,
	useGetConversionCreateMutation,
	useGetConversionQuery,
	useTransferCreateMutation,
	useWithdrawalCreateMutation,
	useGetBillingTransferQuery,
	useWithdrawalQuery,
	useBillingTransactionsListQuery,
	useBillingApproveMutation,
	useBillingRejectMutation,
	useBillingConfirmMutation,
	useBillingReceiveMutation,
} = userBillingApi;
